import {
  CAMPAIGN_MESSAGE_TEMPLATE_LIMITS,
  type CampaignPurpose,
  type CampaignRecord,
  type CampaignTemplateVariableConfig,
  type SegmentFilter,
} from '@workspace/types';
import {
  DEFAULT_CAMPAIGN_SCHEDULE,
  type CampaignScheduleState,
} from '../components/ContactAgentCampaignScheduleFields.utils';
import { defaultCampaignEndDateInput } from './campaignWizardRules';

export function cloneCampaignName(name: string): string {
  const bounded = name.slice(0, 240);
  return `Copia de ${bounded}`.slice(0, 255);
}

export function scheduleFromCampaignRecord(campaign: CampaignRecord): CampaignScheduleState {
  return {
    ...DEFAULT_CAMPAIGN_SCHEDULE,
    scheduleKind: campaign.scheduleKind === 'RURAL' ? 'RURAL' : 'URBANO',
    weekdayEnabled: campaign.businessDays.some(day => day >= 1 && day <= 5),
    weekdayFrom: campaign.scheduleFrom || DEFAULT_CAMPAIGN_SCHEDULE.weekdayFrom,
    weekdayTo: campaign.scheduleTo || DEFAULT_CAMPAIGN_SCHEDULE.weekdayTo,
    weekdayAfternoonFrom: campaign.afternoonFrom || DEFAULT_CAMPAIGN_SCHEDULE.weekdayAfternoonFrom,
    weekdayAfternoonTo: campaign.afternoonTo || DEFAULT_CAMPAIGN_SCHEDULE.weekdayAfternoonTo,
    saturdayEnabled: Boolean(campaign.saturdayFrom && campaign.saturdayTo),
    saturdayFrom: campaign.saturdayFrom || DEFAULT_CAMPAIGN_SCHEDULE.saturdayFrom,
    saturdayTo: campaign.saturdayTo || DEFAULT_CAMPAIGN_SCHEDULE.saturdayTo,
    saturdayAfternoonFrom:
      campaign.saturdayAfternoonFrom || DEFAULT_CAMPAIGN_SCHEDULE.saturdayAfternoonFrom,
    saturdayAfternoonTo: campaign.saturdayAfternoonTo || DEFAULT_CAMPAIGN_SCHEDULE.saturdayAfternoonTo,
    sundayEnabled: Boolean(campaign.sundayFrom && campaign.sundayTo),
    sundayFrom: campaign.sundayFrom || DEFAULT_CAMPAIGN_SCHEDULE.sundayFrom,
    sundayTo: campaign.sundayTo || DEFAULT_CAMPAIGN_SCHEDULE.sundayTo,
    sundayAfternoonFrom: campaign.sundayAfternoonFrom || DEFAULT_CAMPAIGN_SCHEDULE.sundayAfternoonFrom,
    sundayAfternoonTo: campaign.sundayAfternoonTo || DEFAULT_CAMPAIGN_SCHEDULE.sundayAfternoonTo,
    holidays: (campaign.holidays ?? []).slice(0, 100),
  };
}

export function cloneCampaignDraft(campaign: CampaignRecord): {
  name: string;
  agentId: string;
  channelId: string;
  purpose: CampaignPurpose;
  deliveryMode: CampaignRecord['deliveryMode'];
  channelKind: CampaignRecord['channelKind'];
  messageTemplateId: string;
  initialTemplateId: string;
  retryTemplateId: string;
  retryWaitHours: number;
  templateVariableMap: CampaignTemplateVariableConfig;
  maxContactsPerDay: number;
  segmentFilter: SegmentFilter;
  schedule: CampaignScheduleState;
  autoReplyText: string;
  endsAtDate: string;
} {
  const retryOrder = campaign.channelKind === 'EMAIL' ? 0 : 1;
  const retry = campaign.steps?.find(step => step.stepOrder === retryOrder);
  return {
    name: cloneCampaignName(campaign.name),
    agentId: campaign.agentId ?? '',
    channelId: campaign.whatsAppChannelId ?? '',
    purpose: campaign.purpose === 'AVISO' ? 'AVISO' : 'COBRANZAS',
    deliveryMode: campaign.deliveryMode === 'NOTIFICATION' ? 'NOTIFICATION' : 'CONVERSATIONAL',
    channelKind: campaign.channelKind === 'SMS' || campaign.channelKind === 'EMAIL' ? campaign.channelKind : 'WHATSAPP',
    messageTemplateId: campaign.messageTemplateId ?? '',
    initialTemplateId: campaign.initialTemplateId ?? '',
    retryTemplateId: retry?.templateId ?? '',
    retryWaitHours: retry?.waitHours ?? 24,
    templateVariableMap: campaign.templateVariableMap ?? {},
    maxContactsPerDay: campaign.maxContactsPerDay,
    segmentFilter: campaign.segmentFilter ?? { overdueOnly: true },
    schedule: scheduleFromCampaignRecord(campaign),
    autoReplyText: (campaign.autoReplyText ?? '').slice(0, CAMPAIGN_MESSAGE_TEMPLATE_LIMITS.autoReplyMax),
    endsAtDate: defaultCampaignEndDateInput(),
  };
}
